'use client';
import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

interface InstitutionFormData {
  school_name: string;
  contact_person: string;
  position: string;
  email: string;
  phone: string;
  school_location: string;
  number_of_students: string;
  number_of_teachers: string;
  preferred_go_live_date: string;
  additional_notes: string;
}

const initialForm: InstitutionFormData = {
  school_name: '',
  contact_person: '',
  position: '',
  email: '',
  phone: '',
  school_location: '',
  number_of_students: '',
  number_of_teachers: '',
  preferred_go_live_date: '',
  additional_notes: '',
};

interface InstitutionEnquiryModalProps {
  open: boolean;
  onClose: () => void;
}

export function InstitutionEnquiryModal({ open, onClose }: InstitutionEnquiryModalProps) {
  const [form, setForm] = useState<InstitutionFormData>(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const handleChange = (field: keyof InstitutionFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.school_name || !form.contact_person || !form.email || !form.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.from('institution_enquiries').insert({
        school_name: form.school_name,
        contact_person: form.contact_person,
        position: form.position,
        email: form.email,
        phone: form.phone,
        school_location: form.school_location,
        number_of_students: parseInt(form.number_of_students) || 0,
        number_of_teachers: parseInt(form.number_of_teachers) || 0,
        preferred_go_live_date: form.preferred_go_live_date || null,
        additional_notes: form.additional_notes,
        status: 'pending',
      });
      if (error) throw error;
      setSubmitted(true);
      toast.success('Enquiry submitted successfully!');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <h2 className="text-xl font-extrabold text-foreground">Register Your School</h2>
            <p className="text-sm text-muted-foreground">Institution Plan — $499/month</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-muted flex items-center justify-center transition-colors">
            <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-20 h-20 gradient-card-green rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={40} className="text-white" />
            </div>
            <h3 className="text-2xl font-extrabold text-foreground mb-3">Enquiry Received!</h3>
            <p className="text-muted-foreground leading-relaxed max-w-md mx-auto mb-6">
              Thank you for your interest in the Nerdlora Institution Plan. Our team will review your enquiry and contact you within 2–3 business days to discuss onboarding for <strong>{form.school_name}</strong>.
            </p>
            <button onClick={onClose} className="px-6 py-3 gradient-purple text-white rounded-xl font-semibold hover:opacity-90 transition-all">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1 md:col-span-2">
                <label className="text-sm font-semibold text-foreground">School Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  value={form.school_name}
                  onChange={(e) => handleChange('school_name', e.target.value)}
                  placeholder="e.g. Accra Academy Senior High School"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Contact Person <span className="text-danger">*</span></label>
                <input
                  type="text"
                  value={form.contact_person}
                  onChange={(e) => handleChange('contact_person', e.target.value)}
                  placeholder="Full name"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Position / Title</label>
                <input
                  type="text"
                  value={form.position}
                  onChange={(e) => handleChange('position', e.target.value)}
                  placeholder="e.g. Headmaster, IT Coordinator"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Email Address <span className="text-danger">*</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="school@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Phone Number <span className="text-danger">*</span></label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+233 XX XXX XXXX"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  required
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="text-sm font-semibold text-foreground">School Location</label>
                <input
                  type="text"
                  value={form.school_location}
                  onChange={(e) => handleChange('school_location', e.target.value)}
                  placeholder="e.g. Accra, Greater Accra Region"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Number of Students</label>
                <input
                  type="number"
                  value={form.number_of_students}
                  onChange={(e) => handleChange('number_of_students', e.target.value)}
                  placeholder="e.g. 500"
                  min="1"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground">Number of Teachers</label>
                <input
                  type="number"
                  value={form.number_of_teachers}
                  onChange={(e) => handleChange('number_of_teachers', e.target.value)}
                  placeholder="e.g. 40"
                  min="1"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="text-sm font-semibold text-foreground">Preferred Go-Live Date</label>
                <input
                  type="date"
                  value={form.preferred_go_live_date}
                  onChange={(e) => handleChange('preferred_go_live_date', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="text-sm font-semibold text-foreground">Additional Notes</label>
                <textarea
                  value={form.additional_notes}
                  onChange={(e) => handleChange('additional_notes', e.target.value)}
                  placeholder="Any specific requirements or questions..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 gradient-purple text-white rounded-xl text-sm font-bold hover:opacity-90 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</>
              ) : (
                <><Icon name="PaperAirplaneIcon" size={16} />Submit Enquiry</>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const institutionFeatures = [
  'Customized School Dashboard',
  'Student Management',
  'Teacher Accounts',
  'School Branding',
  'Institution Reporting',
  'Dedicated School Portal',
  'Future School Management System Integration',
  'Priority Technical Support',
];

export default function CoursesSection() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-sm font-semibold text-primary mb-2 uppercase tracking-widest">Flexible Access</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-foreground">
            Explore Our <span className="text-gradient">Courses</span>
          </h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            No subscriptions required. Browse free courses or purchase individual premium courses — you only pay for what you choose.
          </p>
        </div>

        {/* Course access model cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto mb-16">
          <div className="bg-white rounded-2xl border border-border shadow-card p-8 flex flex-col">
            <div className="w-12 h-12 gradient-card-green rounded-2xl flex items-center justify-center mb-5">
              <Icon name="GiftIcon" size={22} className="text-white" />
            </div>
            <h3 className="text-xl font-extrabold text-foreground mb-2">Free Courses</h3>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-1">
              Many courses on Nerdlora are completely free. Create an account and start learning immediately — no payment required.
            </p>
            <ul className="space-y-2 mb-6">
              {['No credit card needed', 'Instant access', 'Earn completion certificates', 'Mobile & desktop access'].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0" />
                  <span className="text-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <a href="/sign-up-login-screen" className="block text-center py-3 px-6 rounded-xl font-semibold text-sm gradient-purple text-white hover:opacity-90 transition-all active:scale-95">
              Browse Free Courses
            </a>
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-card p-8 flex flex-col">
            <div className="w-12 h-12 gradient-card-blue rounded-2xl flex items-center justify-center mb-5">
              <Icon name="ShoppingBagIcon" size={22} className="text-white" />
            </div>
            <h3 className="text-xl font-extrabold text-foreground mb-2">Premium Courses</h3>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-1">
              Premium courses are purchased individually. Pay only for the specific courses you want — no monthly subscription, no lock-in.
            </p>
            <ul className="space-y-2 mb-6">
              {['One-time course purchase', 'Lifetime access', 'Downloadable resources', 'Certificate of completion'].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0" />
                  <span className="text-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <a href="/sign-up-login-screen" className="block text-center py-3 px-6 rounded-xl font-semibold text-sm border border-primary text-primary hover:bg-secondary transition-all active:scale-95">
              Browse Premium Courses
            </a>
          </div>
        </div>

        {/* Institution Plan */}
        <div className="max-w-3xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden gradient-card-purple p-8 md:p-10 text-white shadow-purple">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
                <div>
                  <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                    <Icon name="BuildingOffice2Icon" size={12} />
                    For Senior High Schools Only
                  </span>
                  <h3 className="text-2xl md:text-3xl font-extrabold mb-2">Institution Plan</h3>
                  <p className="text-white/80 text-sm max-w-md leading-relaxed">
                    A dedicated learning environment exclusively for Senior High Schools. Empower your students and teachers with a customized Nerdlora portal.
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-4xl font-extrabold tabular-nums">$499</p>
                  <p className="text-white/70 text-sm">/month</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {institutionFeatures.map((feature) => (
                  <div key={feature} className="flex items-center gap-2.5 text-sm">
                    <Icon name="CheckCircleIcon" size={16} className="text-white/90 flex-shrink-0" />
                    <span className="text-white/90">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setModalOpen(true)}
                  className="flex-1 py-3.5 px-6 bg-white text-primary rounded-xl font-bold text-sm hover:bg-white/90 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <Icon name="BuildingOffice2Icon" size={16} />
                  Register Your School
                </button>
                <button
                  onClick={() => setModalOpen(true)}
                  className="flex-1 py-3.5 px-6 bg-white/20 text-white rounded-xl font-bold text-sm hover:bg-white/30 transition-all active:scale-95 flex items-center justify-center gap-2 border border-white/30"
                >
                  <Icon name="PlayCircleIcon" size={16} />
                  Request a School Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <InstitutionEnquiryModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}